import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, normalizeStyle as _normalizeStyle, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createBlock as _createBlock, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "top-sticky-container"
}
const _hoisted_2 = { class: "body-container" }

import { computed, ref, onMounted, onUnmounted, nextTick, watch, markRaw, shallowRef } from 'vue';
import { LoadingStatus } from './types';
import EzTable from './EzTable.vue';
import EzTableRow from './EzTableRow.vue';

/**
 * 仮想スクロール用のアイテムデータ型
 */
interface VirtualItem {
  /** アイテムの一意識別子 */
  id: string;
  /** アイテムのデータ */
  data: any;
  /** アイテムの高さ（px） */
  height: number;
  /** アイテムのタイプ（parent/child/loading/error） */
  type: 'parent' | 'child' | 'loading' | 'error' | 'zeroMatch';
  /** 親アイテムのID（子アイテムの場合） */
  parentId?: string;
  /** インデックス位置 */
  index: number;
}

/**
 * 表示範囲の情報
 */
interface VisibleRange {
  /** 開始インデックス */
  startIndex: number;
  /** 終了インデックス */
  endIndex: number;
  /** バッファを含む開始インデックス */
  bufferedStartIndex: number;
  /** バッファを含む終了インデックス */
  bufferedEndIndex: number;
}

/**
 * VirtualizedEzTableコンポーネントのプロパティ
 * @property {any[]} items - 表示するデータ配列
 * @property {number} itemHeight - 各アイテムの固定高さ（px）
 * @property {number} containerHeight - コンテナの高さ（px）
 * @property {number} bufferSize - バッファサイズ（表示外でも事前レンダリングするアイテム数）
 * @property {string[]} gridTemplateColumns - グリッドテンプレート列
 * @property {number[]} stickyColumnIndexes - 固定列のインデックス配列
 * @property {Set<string>} expandedItems - 展開されたアイテムのIDセット
 * @property {Function} getChildItems - 各アイテムの子要素を取得する関数
 * @property {LoadingStatus} loadingStatus - テーブルのローディング状態
 */
interface Props {
  items: any[];
  itemHeight?: number;
  containerHeight?: number;
  bufferSize?: number;
  gridTemplateColumns: string[];
  stickyColumnIndexes?: number[];
  expandedItems?: Set<string>;
  getChildItems?: (item: any) => any[];
  loadingStatus?: LoadingStatus;
}

// Props定義

export default /*@__PURE__*/_defineComponent({
  __name: 'VirtualizedEzTable',
  props: {
    items: {},
    itemHeight: { default: 48 },
    containerHeight: { default: 400 },
    bufferSize: { default: 3 },
    gridTemplateColumns: {},
    stickyColumnIndexes: { default: () => [] },
    expandedItems: { default: () => new Set() },
    getChildItems: { type: Function, default: () => [] },
    loadingStatus: { default: () => ({ type: 'success' }) }
  },
  emits: ["horizontalScroll", "scroll", "itemClick", "toggle", "retry"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

// Events定義
const emits = __emit;

// リアクティブデータ
const tableRef = ref<HTMLElement>();
const scrollTop = ref(0);
const lastScrollTop = ref(0);
const scrollDirection = ref<'up' | 'down' | 'none'>('none');
const isScrolling = ref(false);

// タイマー管理を最適化
let scrollTimeout: number | undefined;
let throttleTimeout: number | undefined;
let animationFrameId: number | undefined;

/**
 * 仮想化されたアイテムリストを生成
 */
const virtualItems = computed((): VirtualItem[] => {
  const items = props.items;
  const expandedItems = props.expandedItems;
  const getChildItems = props.getChildItems;
  const itemHeight = props.itemHeight;

  // 事前にサイズを計算してメモリ割り当てを最適化
  let estimatedSize = items.length;
  for (const item of items) {
    const itemId = item.businessId || item.id;
    if (itemId && expandedItems.has(itemId)) {
      const children = getChildItems(item);
      estimatedSize += Math.max(1, children.length); // loading/error or children
    }
  }

  const result: VirtualItem[] = new Array(estimatedSize);
  let resultIndex = 0;
  let globalIndex = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const itemId = item.businessId || item.id || String(i);

    // 親アイテムを追加（markRawで非リアクティブ化）
    result[resultIndex++] = markRaw({
      id: itemId,
      data: item, // データオブジェクト自体は生のまま
      height: itemHeight,
      type: 'parent',
      index: globalIndex++,
    });

    // 展開されている場合は子アイテムを追加
    if (expandedItems.has(itemId)) {
      if (item.childrenAsyncStatus === 'error') {
        result[resultIndex++] = markRaw({
          id: `${itemId}-child-error`,
          data: undefined,
          height: itemHeight,
          type: 'error',
          parentId: itemId,
          index: globalIndex++,
        });
      } else if (item.childrenAsyncStatus === 'loading') {
        result[resultIndex++] = markRaw({
          id: `${itemId}-child-loading`,
          data: undefined,
          height: itemHeight,
          type: 'loading',
          parentId: itemId,
          index: globalIndex++,
        });
      } else {
        const children = getChildItems(item);
        if (children.length === 0) {
          result[resultIndex++] = markRaw({
            id: `${itemId}-child-zero-match`,
            data: undefined,
            height: itemHeight,
            type: 'zeroMatch',
            parentId: itemId,
            index: globalIndex++,
          });
        } else {
          for (let j = 0; j < children.length; j++) {
            const child = children[j];
            result[resultIndex++] = markRaw({
              id: child.businessId || child.id || `${itemId}-child-${j}`,
              data: child,
              height: itemHeight,
              type: 'child',
              parentId: itemId,
              index: globalIndex++,
            });
          }
        }
      }
    }
  }

  // 実際のサイズにトリミング
  result.length = resultIndex;
  return result;
});

/**
 * 総高さを計算
 */
const totalHeight = computed((): number => {
  return virtualItems.value.length * props.itemHeight;
});
/**
 * 表示範囲を計算
 */
const visibleRange = computed((): VisibleRange => {
  const containerHeight = props.containerHeight;
  const itemHeight = props.itemHeight;
  const totalItems = virtualItems.value.length;
  const currentScrollTop = scrollTop.value;

  // 早期リターンで計算負荷を削減
  if (totalItems === 0) {
    return {
      startIndex: 0,
      endIndex: 0,
      bufferedStartIndex: 0,
      bufferedEndIndex: 0,
    };
  }

  // 数学的計算を最小限に
  const startIndex = Math.floor(currentScrollTop / itemHeight);
  const visibleCount = Math.ceil(containerHeight / itemHeight);
  const endIndex = Math.min(startIndex + visibleCount, totalItems - 1);

  // 固定バッファサイズ（計算負荷を削減）
  const bufferSize = props.bufferSize;
  const bufferedStartIndex = Math.max(0, startIndex - bufferSize);
  const bufferedEndIndex = Math.min(totalItems - 1, endIndex + bufferSize);

  return {
    startIndex,
    endIndex,
    bufferedStartIndex,
    bufferedEndIndex,
  };
});

/**
 * 表示する仮想アイテム
 */
const visibleItems = computed((): VirtualItem[] => {
  const range = visibleRange.value;
  const items = virtualItems.value;

  // sliceの代わりに直接配列を作成（メモリ効率向上）
  const start = range.bufferedStartIndex;
  const end = range.bufferedEndIndex;
  const length = end - start + 1;

  if (length <= 0) return [];

  const result = new Array(length);
  for (let i = 0; i < length; i++) {
    result[i] = items[start + i];
  }

  return result;
});

/**
 * 表示領域のオフセット
 */
const offsetY = computed((): number => {
  return visibleRange.value.bufferedStartIndex * props.itemHeight;
});

/**
 * スクロールイベントハンドラ
 * RequestAnimationFrameとスロットリングを組み合わせ
 * @param event - スクロールイベントオブジェクト
 */
const onScroll = (event: Event) => {
  if (!event.target) return;

  // 既存のタイマーをキャンセル（最小限に）
  if (animationFrameId !== undefined) {
    cancelAnimationFrame(animationFrameId);
  }

  animationFrameId = requestAnimationFrame(() => {
    const target = event.target as HTMLElement;
    const newScrollTop = target.scrollTop;

    // スクロール方向の検出
    if (Math.abs(newScrollTop - lastScrollTop.value) > 1) {
      // 微小な変化は無視
      scrollDirection.value = newScrollTop > lastScrollTop.value ? 'down' : 'up';
    }

    lastScrollTop.value = scrollTop.value;
    scrollTop.value = newScrollTop;

    // スクロール状態管理（スロットリング付き）
    if (!isScrolling.value) {
      isScrolling.value = true;
    }

    // デバウンス処理（clearTimeoutの頻度を削減）
    if (scrollTimeout === undefined) {
      scrollTimeout = setTimeout(() => {
        isScrolling.value = false;
        scrollTimeout = undefined; // タイマーIDをクリア
      }, 100);
    }

    // スロットリング付きイベント発火（30FPS に制限）
    if (throttleTimeout === undefined) {
      emits('scroll', scrollTop.value);
      throttleTimeout = setTimeout(() => {
        throttleTimeout = undefined;
      }, 32);
    }

    animationFrameId = undefined; // アニメーションフレームIDをクリア
  });
};

/**
 * アイテムクリックハンドラ
 * @param item - クリックされた仮想アイテム
 * @param event - マウスイベントオブジェクト
 */
const handleItemClick = (item: VirtualItem, event: MouseEvent) => {
  emits('itemClick', item.data, event);
};

/**
 * 展開/折りたたみハンドラ
 * @param itemId - 展開/折りたたみを行うアイテムのID
 */
const handleToggle = (itemId: string) => {
  emits('toggle', itemId);
};

/**
 * 固定列の左位置を計算
 * @param columnIndex - 計算対象の列インデックス
 * @returns 左位置のピクセル値（例: '50px'）
 */
const getLeftPosition = (columnIndex: number): string => {
  let leftPos = 0;

  for (let i = 0; i < columnIndex; i++) {
    if (props.stickyColumnIndexes.includes(i)) {
      const width = parseInt(props.gridTemplateColumns[i]?.replace('px', '') || '0', 10);
      leftPos += width;
    }
  }

  return `${leftPos}px`;
};

/**
 * gridTemplateColumnsから横幅（px）を計算する
 * fr単位は現状無視し、pxのみ合計する
 * 小数点付きpx値にも対応
 * @returns 横幅のピクセル値（例: '600px'）
 */
const calculatedTableWidth = computed((): { scrollable: string; sticky: string } => {
  // px単位の列幅のみ合計（小数点も考慮）
  const pxSum = splitGridTemplateColumns.value.scrollableGridTemplateColumns.reduce((sum, col) => {
    const match = col.match(/([\d.]+)px/);
    if (match) {
      return sum + parseFloat(match[1]);
    }
    return sum;
  }, 0);
  const pxStickySum = splitGridTemplateColumns.value.stickyGridTemplateColumns.reduce((sum, col) => {
    const match = col.match(/([\d.]+)px/);
    if (match) {
      return sum + parseFloat(match[1]);
    }
    return sum;
  }, 0);
  // pxが1つもなければ100%にフォールバック
  return { scrollable: pxSum > 0 ? `${pxSum}px` : '100%', sticky: pxStickySum > 0 ? `${pxStickySum}px` : '0' };
});

// ライフサイクル
onMounted(() => {
  // テンプレートでの@scrollバインディングを使用するため、
  // 追加のイベントリスナー設定は不要
});

onUnmounted(() => {
  // すべてのタイマーとアニメーションフレームをクリーンアップ
  if (scrollTimeout !== undefined) {
    clearTimeout(scrollTimeout);
    scrollTimeout = undefined;
  }
  if (throttleTimeout !== undefined) {
    clearTimeout(throttleTimeout);
    throttleTimeout = undefined;
  }
  if (animationFrameId !== undefined) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = undefined;
  }
});

// 外部からアクセス可能なメソッド
__expose({
  /**
   * 指定されたアイテムまでスクロール
   * @param itemId - スクロール先のアイテムID
   */
  scrollToItem: (itemId: string) => {
    const itemIndex = virtualItems.value.findIndex((item) => item.id === itemId);
    if (itemIndex >= 0 && tableRef.value) {
      const targetScrollTop = itemIndex * props.itemHeight;
      tableRef.value.scrollTop = targetScrollTop;
    }
  },

  /**
   * 先頭にスクロール
   */
  scrollToTop: () => {
    if (tableRef.value) {
      tableRef.value.scrollTop = 0;
    }
  },

  /**
   * 末尾にスクロール
   */
  scrollToBottom: () => {
    if (tableRef.value) {
      tableRef.value.scrollTop = totalHeight.value;
    }
  },
});

const splitGridTemplateColumns = computed(() => {
  const stickyIndexSet = new Set(props.stickyColumnIndexes);
  const stickyGridTemplateColumns: string[] = [];
  const scrollableGridTemplateColumns: string[] = [];
  props.gridTemplateColumns.forEach((width, index) => {
    if (stickyIndexSet.has(index)) {
      stickyGridTemplateColumns.push(width);
    } else {
      scrollableGridTemplateColumns.push(width);
    }
  });
  return { stickyGridTemplateColumns, scrollableGridTemplateColumns };
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(EzTable, {
    ref_key: "tableRef",
    ref: tableRef,
    "grid-template-columns": _ctx.gridTemplateColumns,
    "sticky-header": true,
    "sticky-left-columns": true,
    "sticky-column-indexes": _ctx.stickyColumnIndexes,
    "loading-status": _ctx.loadingStatus,
    "max-height": `${_ctx.containerHeight}px`,
    "is-vertical-scrollable": totalHeight.value > _ctx.containerHeight,
    class: "virtualized-ez-table",
    onRetry: _cache[0] || (_cache[0] = ($event: any) => (emits('retry'))),
    onScroll: _cache[1] || (_cache[1] = 
      (e) => {
        onScroll(e);
      }
    )
  }, {
    header: _withCtx(() => [
      _renderSlot(_ctx.$slots, "header")
    ]),
    body: _withCtx(() => [
      _renderSlot(_ctx.$slots, "body-root"),
      (_ctx.$slots['row-top-sticky'])
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(EzTableRow, {
              "grid-template-columns": _ctx.gridTemplateColumns,
              style: _normalizeStyle({ height: `${props.itemHeight}px` })
            }, {
              default: _withCtx(() => [
                _renderSlot(_ctx.$slots, "row-top-sticky")
              ], undefined, true),
              _: 3
            }, 8, ["grid-template-columns", "style"])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", {
          ref: "stickyContainerRef",
          class: "sticky-container virtual-scroll-container",
          style: _normalizeStyle({
            height: `${_ctx.containerHeight}px`,
            width: calculatedTableWidth.value.sticky,
          })
        }, [
          _createElementVNode("div", {
            class: "virtual-spacer",
            style: _normalizeStyle({ height: `${totalHeight.value}px`, position: 'relative', width: calculatedTableWidth.value.sticky, zIndex: 2 })
          }, [
            _createElementVNode("div", {
              class: "virtual-content",
              style: _normalizeStyle({
                transform: `translateY(${offsetY.value}px)`,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                width: calculatedTableWidth.value.sticky,
              })
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleItems.value, (virtualItem) => {
                return (_openBlock(), _createBlock(EzTableRow, {
                  key: virtualItem.id,
                  "grid-template-columns": splitGridTemplateColumns.value.stickyGridTemplateColumns,
                  class: _normalizeClass({
                  'virtual-table-row': true,
                  'virtual-table-row--parent': virtualItem.type === 'parent',
                  'virtual-table-row--child': virtualItem.type === 'child',
                  'virtual-table-row--loading': virtualItem.type === 'loading',
                  'virtual-table-row--error': virtualItem.type === 'error',
                }),
                  style: _normalizeStyle({ height: `${virtualItem.height}px` }),
                  onClick: ($event: any) => (handleItemClick(virtualItem, $event))
                }, {
                  default: _withCtx(() => [
                    _renderSlot(_ctx.$slots, "row-sticky", {
                      item: virtualItem.data,
                      virtualItem: virtualItem,
                      getLeftPosition: getLeftPosition,
                      handleToggle: handleToggle
                    })
                  ], undefined, true),
                  _: 2
                }, 1032, ["grid-template-columns", "class", "style", "onClick"]))
              }), 128))
            ], 4)
          ], 4)
        ], 4),
        _createElementVNode("div", {
          ref: "containerRef",
          class: "virtual-scroll-container",
          style: _normalizeStyle({
            height: `${_ctx.containerHeight}px`,
            position: 'relative',
            //overflowY: 'auto',
            width: calculatedTableWidth.value.scrollable,
          })
        }, [
          _createElementVNode("div", {
            class: "virtual-spacer",
            style: _normalizeStyle({ height: `${totalHeight.value}px`, position: 'relative', width: calculatedTableWidth.value.scrollable })
          }, [
            _createElementVNode("div", {
              class: "virtual-content",
              style: _normalizeStyle({
                transform: `translateY(${offsetY.value}px)`,
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                width: calculatedTableWidth.value.scrollable,
              })
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleItems.value, (virtualItem) => {
                return (_openBlock(), _createBlock(EzTableRow, {
                  key: virtualItem.id,
                  "grid-template-columns": 
                  virtualItem.type === 'loading' || virtualItem.type === 'error' || virtualItem.type === 'zeroMatch'
                    ? ['auto']
                    : splitGridTemplateColumns.value.scrollableGridTemplateColumns
                ,
                  class: _normalizeClass({
                  'virtual-table-row': true,
                  'virtual-table-row--parent': virtualItem.type === 'parent',
                  'virtual-table-row--child': virtualItem.type === 'child',
                  'virtual-table-row--loading': virtualItem.type === 'loading',
                  'virtual-table-row--error': virtualItem.type === 'error',
                }),
                  style: _normalizeStyle({ height: `${virtualItem.height}px` }),
                  onClick: ($event: any) => (handleItemClick(virtualItem, $event))
                }, {
                  default: _withCtx(() => [
                    _renderSlot(_ctx.$slots, "row", {
                      item: virtualItem.data,
                      virtualItem: virtualItem,
                      getLeftPosition: getLeftPosition,
                      handleToggle: handleToggle
                    })
                  ], undefined, true),
                  _: 2
                }, 1032, ["grid-template-columns", "class", "style", "onClick"]))
              }), 128))
            ], 4)
          ], 4)
        ], 4)
      ])
    ]),
    loading: _withCtx(() => [
      _renderSlot(_ctx.$slots, "loading")
    ]),
    error: _withCtx(({ errorMessage, retry }) => [
      _renderSlot(_ctx.$slots, "error", {
        errorMessage: errorMessage,
        retry: retry
      })
    ]),
    "zero-match": _withCtx(() => [
      _renderSlot(_ctx.$slots, "zero-match")
    ]),
    _: 3
  }, 8, ["grid-template-columns", "sticky-column-indexes", "loading-status", "max-height", "is-vertical-scrollable"]))
}
}

})